import type {
  ResourceScheduleDay,
  ResourceScheduleSlot,
  ResourceScheduleSlotDuration,
  AvailabilityCalendarResponse,
} from '@/services/naaviApi/types';
import { normalizeTimeLoose } from '@/helpers/timeHelper';

type SlotPriceByDuration = NonNullable<ResourceScheduleSlot['priceByDuration']>;

function mergeTimes(existing?: string[] | null, incoming?: string[] | null): string[] {
  const registry = new Set<string>();

  const append = (collection?: string[] | null) => {
    if (!Array.isArray(collection)) {
      return;
    }
    collection.forEach((time) => {
      const normalized = normalizeTimeLoose(time);
      if (normalized) {
        registry.add(normalized);
      }
    });
  };

  append(existing);
  append(incoming);

  return Array.from(registry.values()).sort();
}

function mergeSlotDurations(
  existing?: ResourceScheduleSlotDuration[] | null,
  incoming?: ResourceScheduleSlotDuration[] | null,
): ResourceScheduleSlotDuration[] | undefined {
  const durations = new Map<number, ResourceScheduleSlotDuration>();

  const ingest = (collection?: ResourceScheduleSlotDuration[] | null) => {
    if (!Array.isArray(collection)) {
      return;
    }
    collection.forEach((entry) => {
      if (!entry || typeof entry.duration !== 'number') {
        return;
      }
      durations.set(entry.duration, { ...entry });
    });
  };

  ingest(existing);
  ingest(incoming);

  if (!durations.size) {
    return undefined;
  }

  return Array.from(durations.values()).sort((a, b) => a.duration - b.duration);
}

function mergeSlotPriceByDuration(
  existing?: SlotPriceByDuration | null,
  incoming?: SlotPriceByDuration | null,
): SlotPriceByDuration | undefined {
  if (!existing && !incoming) {
    return undefined;
  }

  return {
    ...(existing ?? {}),
    ...(incoming ?? {}),
  };
}

function mergeSlots(
  existing?: ResourceScheduleSlot[] | null,
  incoming?: ResourceScheduleSlot[] | null,
): ResourceScheduleSlot[] {
  const slots = new Map<string, ResourceScheduleSlot>();

  const ingest = (collection?: ResourceScheduleSlot[] | null) => {
    if (!Array.isArray(collection)) {
      return;
    }

    collection.forEach((slot) => {
      if (!slot || typeof slot.time !== 'string') {
        return;
      }
      const timeKey = normalizeTimeLoose(slot.time);
      if (!timeKey) {
        return;
      }

      const current = slots.get(timeKey);
      const mergedDurations = mergeSlotDurations(current?.durations, slot.durations);
      const mergedPriceByDuration = mergeSlotPriceByDuration(
        current?.priceByDuration,
        slot.priceByDuration,
      );

      const nextSlot: ResourceScheduleSlot = {
        ...(current ?? {}),
        ...slot,
        time: timeKey,
      };

      if (mergedDurations) {
        nextSlot.durations = mergedDurations;
      } else {
        delete (nextSlot as Partial<ResourceScheduleSlot>).durations;
      }

      if (mergedPriceByDuration && Object.keys(mergedPriceByDuration).length) {
        nextSlot.priceByDuration = mergedPriceByDuration;
      } else {
        delete (nextSlot as Partial<ResourceScheduleSlot>).priceByDuration;
      }

      slots.set(timeKey, nextSlot);
    });
  };

  ingest(existing);
  ingest(incoming);

  return Array.from(slots.values()).sort((a, b) => a.time.localeCompare(b.time));
}

export function mergeResourceSchedules(
  current?: ResourceScheduleDay[] | null,
  incoming?: ResourceScheduleDay[] | null,
): ResourceScheduleDay[] {
  const byDate = new Map<string, ResourceScheduleDay>();

  const ingest = (collection?: ResourceScheduleDay[] | null) => {
    if (!Array.isArray(collection)) {
      return;
    }

    collection.forEach((day) => {
      if (!day || typeof day !== 'object' || typeof day.date !== 'string') {
        return;
      }

      const dateKey = day.date;
      const existing = byDate.get(dateKey);

      const mergedTimes = mergeTimes(existing?.times, day.times);
      const mergedSlots = mergeSlots(existing?.slots, day.slots);

      const nextDay: ResourceScheduleDay = {
        ...(existing ?? {}),
        ...day,
        date: dateKey,
        times: mergedTimes,
      };

      if (mergedSlots.length) {
        nextDay.slots = mergedSlots;
      } else {
        delete (nextDay as Partial<ResourceScheduleDay>).slots;
      }

      byDate.set(dateKey, nextDay);
    });
  };

  ingest(current);
  ingest(incoming);

  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}

export function collectScheduleHours(schedule?: ResourceScheduleDay[] | null): string[] {
  if (!Array.isArray(schedule)) {
    return [];
  }

  const hours = new Set<string>();

  schedule.forEach((day) => {
    if (!day) {
      return;
    }

    if (Array.isArray(day.times)) {
      day.times.forEach((time) => {
        const normalized = normalizeTimeLoose(time);
        if (normalized) {
          hours.add(normalized);
        }
      });
    }

    if (Array.isArray(day.slots)) {
      day.slots.forEach((slot) => {
        const normalized = normalizeTimeLoose(slot?.time ?? null);
        if (normalized) {
          hours.add(normalized);
        }
      });
    }
  });

  return Array.from(hours.values()).sort();
}

export function getScheduleMaxTimes(schedule?: ResourceScheduleDay[] | null): number {
  if (!Array.isArray(schedule)) {
    return 0;
  }

  return schedule.reduce((accumulator, day) => {
    if (!day) {
      return accumulator;
    }

    const dayTimes = new Set<string>();

    if (Array.isArray(day.times)) {
      day.times.forEach((time) => {
        const normalized = normalizeTimeLoose(time);
        if (normalized) {
          dayTimes.add(normalized);
        }
      });
    }

    if (Array.isArray(day.slots)) {
      day.slots.forEach((slot) => {
        const normalized = normalizeTimeLoose(slot?.time ?? null);
        if (normalized) {
          dayTimes.add(normalized);
        }
      });
    }

    return Math.max(accumulator, dayTimes.size);
  }, 0);
}

export function resolveCalendarTotals(
  response?: AvailabilityCalendarResponse | null,
  fallbackSchedule?: ResourceScheduleDay[] | null,
): { dates: number; times: number } {
  const schedule = Array.isArray(fallbackSchedule) ? fallbackSchedule : [];
  const fallbackDates = schedule.length;
  const fallbackTimes = getScheduleMaxTimes(schedule);

  return {
    dates: Math.max(Number(response?.totals?.dates ?? 0), fallbackDates),
    times: Math.max(Number(response?.totals?.times ?? 0), fallbackTimes),
  };
}
